import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Send, ShieldCheck } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";

const inputCls = "w-full rounded-md border border-line bg-bg px-3 py-2.5 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber/60";
const Field = ({ label, required, children }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1.5">
      {label}{required && <span className="text-brand-amber ml-0.5">*</span>}
    </div>
    {children}
  </div>
);

function detectClient() {
  const ua = navigator.userAgent;
  const browser =
    /edg\//i.test(ua) ? "Edge" : /chrome|crios/i.test(ua) ? "Chrome"
    : /firefox|fxios/i.test(ua) ? "Firefox" : /safari/i.test(ua) ? "Safari" : "Other";
  const device = /mobi|android|iphone|ipad/i.test(ua) ? (/ipad|tablet/i.test(ua) ? "Tablet" : "Mobile") : "Desktop";
  const os =
    /windows/i.test(ua) ? "Windows" : /mac os/i.test(ua) ? "macOS"
    : /android/i.test(ua) ? "Android" : /iphone|ipad|ios/i.test(ua) ? "iOS"
    : /linux/i.test(ua) ? "Linux" : "Other";
  return { browser, device_type: device, os };
}

export default function SupportNewTicket() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const client = detectClient();
  const [f, setF] = useState({
    subject: "", description: "", category: params.get("category") || "",
    priority: "normal", station_id: "", affected_feature: params.get("feature") || "",
    integration_provider: "", allow_logs: true, allow_impersonation: false,
  });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    api.get("/helpdesk/meta").then((r) => {
      setMeta(r.data);
      if (r.data.stations?.length === 1) set("station_id")(r.data.stations[0].station_id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    if (!f.subject.trim()) { toast.error("Add a subject"); return; }
    if (f.description.trim().length < 10) { toast.error("Describe the issue in a bit more detail"); return; }
    if (!f.category) { toast.error("Pick a support category"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/helpdesk/tickets", { ...f, ...client });
      toast.success(`Ticket ${data.reference} created`);
      navigate(`/dashboard/support/tickets/${data.reference}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create the ticket");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="new-ticket-page">
      <Link to="/dashboard/support/tickets" className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink-primary">
        <ArrowLeft className="h-3.5 w-3.5" /> My tickets
      </Link>
      <PageHeader eyebrow="// SUPPORT" title="Submit a support ticket"
        description="Tell us what's going on — the more detail, the faster we can fix it." />

      <div className="rounded-xl border border-line bg-bg-surface p-5 space-y-4">
        <Field label="Subject" required>
          <input className={inputCls} value={f.subject} onChange={(e) => set("subject")(e.target.value)}
            placeholder="e.g. Instagram posts failing since Tuesday" data-testid="ticket-subject" />
        </Field>
        <Field label="Detailed description" required>
          <textarea className={`${inputCls} min-h-[130px]`} value={f.description}
            onChange={(e) => set("description")(e.target.value)} data-testid="ticket-description"
            placeholder="What did you expect, what happened instead, and any error message you saw…" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Support category" required>
            <select className={inputCls} value={f.category} onChange={(e) => set("category")(e.target.value)} data-testid="ticket-category">
              <option value="">Choose…</option>
              {(meta?.categories || []).map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Priority">
            <select className={inputCls} value={f.priority} onChange={(e) => set("priority")(e.target.value)} data-testid="ticket-priority">
              {(meta?.priorities || ["low", "normal", "high", "urgent"]).map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Station">
            <select className={inputCls} value={f.station_id} onChange={(e) => set("station_id")(e.target.value)} data-testid="ticket-station">
              <option value="">Not station-specific</option>
              {(meta?.stations || []).map((s) => <option key={s.station_id} value={s.station_id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Affected feature">
            <select className={inputCls} value={f.affected_feature} onChange={(e) => set("affected_feature")(e.target.value)} data-testid="ticket-feature">
              <option value="">Choose…</option>
              {(meta?.features || []).map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </Field>
          <Field label="Integration provider">
            <select className={inputCls} value={f.integration_provider} onChange={(e) => set("integration_provider")(e.target.value)} data-testid="ticket-provider">
              <option value="">None / not sure</option>
              {(meta?.providers || []).map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </Field>
        </div>

        <div className="rounded-md border border-line bg-bg-elevated/50 px-3 py-2.5 text-[11px] text-ink-muted" data-testid="ticket-diagnostics">
          <span className="font-mono text-[9px] uppercase tracking-wider text-ink-secondary block mb-1">Included automatically</span>
          Browser: {client.browser} · Device: {client.device_type} · OS: {client.os}. We never collect passwords, tokens, API keys, or payment details.
        </div>

        <label className="flex items-start gap-2.5 cursor-pointer" data-testid="ticket-allow-logs">
          <input type="checkbox" checked={f.allow_logs} onChange={(e) => set("allow_logs")(e.target.checked)}
            className="mt-0.5 accent-[#FF8A1F]" />
          <span className="text-xs text-ink-secondary">Support may review my relevant activity logs to diagnose this issue (credentials are always redacted).</span>
        </label>
        <label className="flex items-start gap-2.5 cursor-pointer" data-testid="ticket-allow-impersonation">
          <input type="checkbox" checked={f.allow_impersonation} onChange={(e) => set("allow_impersonation")(e.target.checked)}
            className="mt-0.5 accent-[#FF8A1F]" />
          <span className="text-xs text-ink-secondary">Support may access my account through secure support impersonation if needed to reproduce the issue.</span>
        </label>

        <div className="flex items-center justify-between pt-2 border-t border-line">
          <span className="inline-flex items-center gap-1.5 text-[10px] text-ink-muted">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-300" /> Only you and support staff can see this ticket
          </span>
          <button type="button" onClick={submit} disabled={busy} data-testid="ticket-submit-btn"
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-5 py-2.5 text-sm font-semibold text-black hover:bg-brand-amber/90 transition disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit ticket
          </button>
        </div>
      </div>
    </div>
  );
}
